/**
 * Module-level memory of which (session, component-type) pairs have already
 * fired the `ui.lumina.shown` analytics event, so the event is emitted at most
 * once per component type per session.
 *
 * Why this exists:
 * Lumina components are rendered as streaming "status components" inside the chat
 * markdown renderer, which mounts/unmounts them repeatedly across stream parts and
 * whenever the surrounding message list re-renders. A component-scoped guard
 * (ref/state) resets on every remount and would still fire duplicate events, so
 * the guard must outlive the component instance. Keying by `sessionId` +
 * `componentType` means each component reports exactly once per answer, no matter
 * how many times it remounts.
 *
 * Memory bound:
 * To avoid unbounded growth over a long session with many answers, the set is
 * capped at `MAX_SHOWN_KEYS` entries with simple FIFO eviction (the oldest
 * inserted key is dropped once the cap is exceeded). `Set` preserves insertion
 * order, so the first entry is always the oldest. Evicting an old key is harmless:
 * at worst it allows one extra "shown" event if that (already-settled) answer's
 * component remounts again much later in the same session.
 *
 * SSR safety:
 * In Node.js the module cache is per-process, not per-request, so a shared set
 * would otherwise leak one request's fired keys into another. This "shown" event
 * is a purely client-side concern (it only fires from a mount effect in the
 * browser), so all reads and writes are gated behind a client-only check. The
 * check is evaluated at call time (not captured once at module init) so that a
 * process which imported this module during SSR still behaves correctly once the
 * client hydrates. On the server the cache is effectively empty and `hasShown`
 * always returns `false`, so no cross-request state is shared.
 */
const isClient = (): boolean => typeof window !== 'undefined';

/**
 * Maximum number of (session, component-type) keys to remember. Kept comfortably
 * above the number of components a user is likely to see in a session, while
 * bounding memory to a small, fixed footprint (a few hundred short strings).
 */
const MAX_SHOWN_KEYS = 500;

const shownKeys = new Set<string>();

/**
 * Builds the composite cache key. `sessionId` may be undefined (e.g. before the
 * final message id has settled); such calls are treated as "not shown" and are
 * never recorded, so no event fires without a joinable id.
 */
const buildKey = (sessionId: string | undefined, componentType: string): string | undefined =>
	sessionId === undefined ? undefined : `${sessionId}::${componentType}`;

/**
 * Returns whether the `ui.lumina.shown` event has already been fired for the
 * given session + component type. Always returns `false` on the server, or when
 * `sessionId` is undefined (see notes above).
 */
export const hasShown = (sessionId: string | undefined, componentType: string): boolean => {
	const key = buildKey(sessionId, componentType);
	return isClient() && key !== undefined && shownKeys.has(key);
};

/**
 * Records that the `ui.lumina.shown` event has been fired for the given session +
 * component type. No-op on the server or when `sessionId` is undefined.
 */
export const markShown = (sessionId: string | undefined, componentType: string): void => {
	const key = buildKey(sessionId, componentType);
	if (!isClient() || key === undefined || shownKeys.has(key)) {
		return;
	}

	// FIFO eviction: drop the oldest entry once the cap is reached so the set stays
	// bounded over long sessions. `Set` iterates in insertion order, so the first
	// value is the oldest.
	if (shownKeys.size >= MAX_SHOWN_KEYS) {
		const oldest = shownKeys.values().next().value;
		if (oldest !== undefined) {
			shownKeys.delete(oldest);
		}
	}

	shownKeys.add(key);
};

/**
 * Internal reset used only to clear the module-level cache.
 *
 * Intentionally named with an `__unsafe` prefix and only meant to be consumed via
 * `shown-event-cache.test-utils.ts` so it cannot leak into production code paths.
 */
export const __unsafeResetShownEventCache = (): void => {
	shownKeys.clear();
};
